/**
 * Centralized Error Handling
 * 
 * Provides utilities for handling API errors consistently across the application.
 */

import axios, { AxiosError } from 'axios'

export interface ApiError {
  message: string
  code?: string
  status?: number
  details?: any
}

/**
 * Extract a user-friendly error message from an axios error
 */
export function getErrorMessage(error: any): string {
  if (error.response?.data?.detail) {
    return error.response.data.detail
  }
  
  if (error.response?.data?.message) {
    return error.response.data.message
  }
  
  if (error.response?.data?.error) {
    return error.response.data.error
  }
  
  if (error.message) {
    return error.message
  }
  
  return 'An unexpected error occurred'
}

/**
 * Convert an axios error to a standardized ApiError object
 */
export function toApiError(error: any): ApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<any>
    
    return {
      message: getErrorMessage(error),
      status: axiosError.response?.status,
      code: axiosError.code,
      details: axiosError.response?.data
    }
  }
  
  return {
    message: getErrorMessage(error),
    details: error
  }
}

/**
 * Check if an error is a network error
 */
export function isNetworkError(error: any): boolean {
  return error.code === 'ECONNABORTED' || 
         error.code === 'ECONNREFUSED' ||
         error.code === 'ENOTFOUND' ||
         error.message?.toLowerCase().includes('network')
}

/**
 * True for any client-side request cancellation (AbortController / axios cancel).
 * Excludes ECONNABORTED — in axios v1 that means timeout, not cancel.
 */
export function isRequestCanceled(error: any): boolean {
  return (
    axios.isCancel(error) ||
    error?.name === 'AbortError' ||
    error?.name === 'CanceledError' ||
    error?.code === 'ERR_CANCELED'
  )
}

/**
 * Check if an error is an authentication error
 */
export function isAuthError(error: any): boolean {
  return error.response?.status === 401
}

/**
 * Check if an error is a permission/authorization error
 */
export function isPermissionError(error: any): boolean {
  return error.response?.status === 403
}

/**
 * Check if an error is a validation error
 */
export function isValidationError(error: any): boolean {
  return error.response?.status === 422 || error.response?.status === 400
}

/**
 * Log an error with appropriate context
 */
export function logError(context: string, error: any): void {
  const apiError = toApiError(error)
  
  console.error(`❌ ${context}:`, {
    message: apiError.message,
    status: apiError.status,
    code: apiError.code,
    details: apiError.details
  })
}

// Re-export axios for error checking
import axios from 'axios'
export { axios }